import { extendTheme, type ThemeConfig } from '@chakra-ui/react'

const themeConfig: ThemeConfig = {
  initialColorMode: "system",
  useSystemColorMode: true,
};

// Shared subtle styling for buttons and tabs
const subtleStyles = {
  bg: "white",
  color: "gray.800",
  border: "1px solid",
  borderColor: "gray.400",
  _hover: { bg: "gray.100" },
  _dark: {
    bg: "gray.700",
    color: "white",
    borderColor: "gray.500",
    _hover: { bg: "gray.600" },
  },
};

const selectedStyles = {
  bg: "brand.500",
  color: "white",
  borderColor: "brand.500",
  _hover: { bg: "brand.600" },
};

const theme = {
  config: themeConfig,
  fonts: {
    heading: "Halant, serif",
    body: "Nunito Sans, sans-serif",
  },
  components: {
    Button: {
      variants: {
        inverse: {
          bg: "gray.200",
          color: "gray.800",
          _hover: { bg: "gray.300" },
          _dark: {
            bg: "gray.700", 
            color: "white", 
            _hover: { bg: "gray.600" }
          }
        },
        subtle: subtleStyles
      }
    },
    Tabs: {
      variants: {
        subtle: {
          tab: {
            ...subtleStyles,
            fontWeight: "semibold",
            borderRadius: "full",
            px: 6,
            py: 3,
            _selected: selectedStyles,
            _dark: {
              ...subtleStyles._dark,
              _selected: selectedStyles,
            }
          },
          tablist: {
            gap: 3,
          }
        }
      }
    }
  },
  styles: {
    global: (props: { colorMode: string }) => ({
      "html, body, #___gatsby, #gatsby-focus-wrapper": {
        height: "100%",
      },
      "html, body": {
        bg: "bodyBg",
        color: props.colorMode === "dark" ? "whiteAlpha.900" : "gray.800",
        transition: "background-color 0.2s, color 0.2s",
      },
      // Improve transitions for color mode changes
      "*, *::before, *::after": {
        transitionProperty: "background-color, border-color, color",
        transitionDuration: "0.2s",
        transitionTimingFunction: "ease-in-out",
      }
    }),
  },
  colors: {
    brand: {
      50: "#ffe1e1",
      100: "#ffb1b1",
      200: "#ff7f7f",
      300: "#ff4c4c",
      400: "#ff1a1a",
      500: "#e60000",
      600: "#b40000",
      700: "#810000",
      800: "#500000",
      900: "#210000",
    },
  },
  semanticTokens: {
    colors: {
      headerBg: { default: "gray.50", _dark: "gray.900" },
      pageBg: { default: "gray.50", _dark: "gray.800" },
      bodyBg: { default: "gray.50", _dark: "gray.800" },
      cardBg: { default: "white", _dark: "gray.700" },
      cardHeaderBg: { default: "gray.100", _dark: "gray.600" },
      cardBorder: { default: "gray.300", _dark: "gray.600" },
      footerBg: { default: "gray.100", _dark: "gray.900" },
      footerText: { default: "gray.600", _dark: "gray.200" },
      eventBg: { default: "white", _dark: "gray.800" },
      eventHeaderBg: { default: "gray.100", _dark: "gray.700" },
      eventHoverBg: { default: "gray.100", _dark: "gray.600" },
      heroBgFrom: { default: "#f7f8f9", _dark: "#1e2329" },
      heroBgTo: { default: "gray.50", _dark: "gray.800" },
      sectionBg: { default: "gray.50", _dark: "gray.800" },
      sectionAltBg: { default: "gray.100", _dark: "gray.700" },
      textLight: { default: "gray.600", _dark: "gray.300" },
      textDark: { default: "gray.800", _dark: "white" },
      brand: { default: "brand.500", _dark: "brand.300" },
    },
  },
};

export default extendTheme(theme);
